// src/lib/search.ts — Fuse.js fuzzy search wrapper

import Fuse from 'fuse.js';
import type { Question, SearchFilters } from '@/types';

let fuseInstance: Fuse<Question> | null = null;

export function initSearch(questions: Question[]): void {
  fuseInstance = new Fuse(questions, {
    keys: [
      { name: 'title', weight: 0.5 },
      { name: 'tags', weight: 0.3 },
      { name: 'shortAnswer', weight: 0.2 },
    ],
    threshold: 0.35,
    includeScore: true,
    minMatchCharLength: 2,
  });
}

export function searchQuestions(query: string, questions: Question[]): Question[] {
  if (!query.trim()) return questions;
  if (!fuseInstance) initSearch(questions);
  return fuseInstance!.search(query).map((r) => r.item);
}

export function applyFilters(questions: Question[], filters: SearchFilters): Question[] {
  let result = [...questions];

  // Text search
  if (filters.query.trim()) {
    result = searchQuestions(filters.query, result);
  }

  // Category filter
  if (filters.categories.length > 0) {
    result = result.filter((q) => filters.categories.includes(q.category));
  }

  // Difficulty filter
  if (filters.difficulties.length > 0) {
    result = result.filter((q) => filters.difficulties.includes(q.difficulty));
  }

  // Tag filter
  if (filters.tags.length > 0) {
    result = result.filter((q) => filters.tags.some((t) => q.tags.includes(t)));
  }

  // Sort
  result = sortQuestions(result, filters.sortBy);

  return result;
}

function sortQuestions(
  questions: Question[],
  sortBy: SearchFilters['sortBy']
): Question[] {
  switch (sortBy) {
    case 'difficulty-asc':
      return [...questions].sort((a, b) => a.difficulty - b.difficulty);
    case 'difficulty-desc':
      return [...questions].sort((a, b) => b.difficulty - a.difficulty);
    case 'alphabetical':
      return [...questions].sort((a, b) => a.title.localeCompare(b.title));
    case 'xp-desc':
      return [...questions].sort((a, b) => b.xpReward - a.xpReward);
    case 'newest':
      return [...questions].sort((a, b) => b.lastUpdated.localeCompare(a.lastUpdated));
    default:
      return questions;
  }
}

export function resetSearch(): void {
  fuseInstance = null;
}
